import type { ReactNode } from 'react';
import './Badge.css';

export type BadgeTone = 'neutral' | 'info' | 'success' | 'warn' | 'danger' | 'accent';

// Tone → the existing status-named badge class, so colors match today's UI.
const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: '',
  info: '',
  success: 'badge-active',
  warn: 'badge-pending',
  danger: 'badge-void',
  accent: 'badge-open',
};

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}

export function Badge({ tone = 'neutral', children, className }: BadgeProps): JSX.Element {
  const cls = ['badge', TONE_CLASS[tone], className].filter(Boolean).join(' ');
  return <span className={cls}>{children}</span>;
}

export interface StatusBadgeProps {
  /** Raw enum value, e.g. "in_progress". */
  status: string;
  children?: ReactNode;
  className?: string;
}

// Drop-in for the `badge badge-${status}` pattern used across list pages.
// Label defaults to a humanized form unless children are supplied.
export function StatusBadge({ status, children, className }: StatusBadgeProps): JSX.Element {
  const cls = ['badge', `badge-${status}`, className].filter(Boolean).join(' ');
  return <span className={cls}>{children ?? humanize(status)}</span>;
}

export type Severity = 'info' | 'low' | 'medium' | 'high' | 'critical';

const SEVERITY_TONE: Record<Severity, BadgeTone> = {
  info: 'neutral',
  low: 'neutral',
  medium: 'warn',
  high: 'warn',
  critical: 'danger',
};

export interface SeverityBadgeProps {
  level: Severity;
  children?: ReactNode;
  /** Show a leading status dot (on by default). */
  dot?: boolean;
  className?: string;
}

export function SeverityBadge({ level, children, dot = true, className }: SeverityBadgeProps): JSX.Element {
  const cls = ['badge', TONE_CLASS[SEVERITY_TONE[level]], 'dsr-badge', className].filter(Boolean).join(' ');
  return (
    <span className={cls}>
      {dot && <span className="dsr-badge-dot" aria-hidden="true" />}
      {children ?? humanize(level)}
    </span>
  );
}

function humanize(s: string): string {
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}
